import React, { useCallback, useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import DragItemTypes from "../DragItemTypes";
import { getEmptyImage } from "react-dnd-html5-backend";
import {
  buildOrganizeViewPath,
  COLLECTION_VIEW,
  getCollections,
  OrganizeViews,
  groupChildrenByParent,
  setIds,
} from "../organizeUtil";
import { any, equals, includes, join, pluck, propEq } from "ramda";
import { useDeleteModuleItems } from "../../modules/moduleServices";
import {
  useAddToAnthology,
  useUpdateAnthologyItems,
} from "../../anthologies/anthologyServices";
import { useParentDrop } from "../use-parentDrop";
import { useDrag } from "react-dnd";
import { notBlank } from "../../../util/ext";
import { useDnD, useSelected, useSidebar } from "../OrganizeProvider";
import { useAnthologyCollectionsCreator } from "../../creatorServices";

import { TreeItem } from "@mui/x-tree-view/TreeItem";

import CollapseIcon from "./CollapseIcon";
import SidebarCollectionTreeItem from "./SidebarCollectionTreeItem";
import SidebarTreeItemLabel from "./SidebarTreeItemLabel";

const useStyles = makeStyles(theme => ({
  anthology: {
    margin: 0,
    fontWeight: "inherit",
  },

  anthologyIcon: {
    color: theme.palette.primary.main,
  },

  avatar: {
    height: 25,
    width: 25,
  },

  content: {
    backgroundColor: "transparent",
  },

  contentHighlighted: {
    backgroundColor: `${theme.palette.primary.main} !important`,
  },

  header: {
    fontWeight: "inherit",
    fontSize: "1.6rem",
  },

  label: {
    color: "inherit",
  },

  labelHighlighted: {
    color: "white",
  },

  labelSubheading: {
    margin: 0,
    fontSize: "1.4rem",
    lineHeight: 1.4,
    fontWeight: "inherit",
  },

  treeItem: {
    fontWeight: "inherit",
  },
}));

const buildAnthologyChildNodeId = ({ module, anthology, collection }) =>
  join("-", [
    OrganizeViews.ORGANIZE,
    OrganizeViews.CONTENT_GROUPS,
    OrganizeViews.MODULE,
    OrganizeViews.ANTHOLOGY,
    OrganizeViews.COLLECTION,
    module.id,
    anthology.id,
    collection.id,
  ]);

const SidebarAnthologyTreeItem = props => {
  const classes = useStyles();
  const {
    child,
    count,
    nestedTree,
    handleLabelClick,
    icon,
    onClick,
    onLabelClick,
    label,
    nodeId,
    nodeType,
    parent,
    parentChildrenList,
    tooltipLabel,
    selected,
    showAlert,
    ...otherProps
  } = props;

  const anthology = child;

  const dnd = useDnD();
  const sidebar = useSidebar();
  const { selectedContentGroups } = useSelected();
  const [collections, setCollections] = useState([]);

  const anthologyCollections = useAnthologyCollectionsCreator(
    anthology.id,
    {
      limit: "all",
      sort: "collection.displayOrder asc",
    },
    { enabled: includes(nodeId, sidebar.expanded) },
  );
  useEffect(() => {
    if (anthologyCollections.isSuccess) {
      setCollections(anthologyCollections.data.contents);
    }
  }, [anthologyCollections.data, anthologyCollections.isSuccess]);

  const getTypographyProps = type => {
    switch (type) {
      case "root":
        return { classes: { root: classes.header } };
      case "anthology":
        return { classes: { root: classes.anthology } };
      default:
        return { classes: { root: classes.treeItem } };
    }
  };

  //-- Handle Drag and Drop Actions
  const addToAnthology = useAddToAnthology();
  const deleteModuleItems = useDeleteModuleItems();
  const updateAnthologyItems = useUpdateAnthologyItems();
  const onDrop = useCallback(
    async ({ items, dropAction }) => {
      try {
        if (equals(dropAction, "move")) {
          const prevParents = groupChildrenByParent(items);

          for (const { prevParent, newChildren } of prevParents) {
            if (notBlank(prevParent.id)) {
              const ids = setIds({
                mediaType: prevParent.mediaType,
                prevMediaList: prevParent.prevMediaList,
                itemsToRemove: newChildren.ids,
              });

              const body = { id: prevParent.id, ids };

              if (equals(prevParent.mediaType, "anthology")) {
                await updateAnthologyItems.mutateAsync({
                  resourceIds: { id: body.id },
                  data: { ids: ids },
                });
              }

              if (equals(prevParent.mediaType, "module")) {
                await deleteModuleItems.mutateAsync(body);
              }
            }
          }
        }

        await addToAnthology.mutateAsync({
          resourceIds: { id: anthology.id },
          data: { ids: pluck("id", items) },
        });

        for (const item of items) {
          if (notBlank(item.onDropSuccess)) {
            item.onDropSuccess();
          }
        }

        const alert = equals(dropAction, "move")
          ? "Collection(s) Successfully Moved"
          : "Collection(s) Successfully Added";

        showAlert("success", [alert]);
      } catch (err) {
        showAlert("error", [err.message]);
      }
    },
    [
      addToAnthology,
      anthology,
      deleteModuleItems,
      showAlert,
      updateAnthologyItems,
    ],
  );

  const [{ isOver, canDrop }, drop] = useParentDrop({
    parent: anthology,
    onDrop,
    accept: [DragItemTypes.COLLECTION],
  });

  // Bug prevented drag when values passed to dep array
  // https://github.com/react-dnd/react-dnd/issues/3479
  const [, drag, preview] = useDrag(
    () => ({
      type: DragItemTypes.ANTHOLOGY,
      item: () => {
        dnd.updateDragAction("Move");
        return {
          id: anthology.id,
          mediaType: "anthology",
          parent,
          prevMediaList: collections,
        };
      },
      end: () => dnd.updateDragAction(""),
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [collections],
  );

  // Create Draggable & Droppable Element
  // https://github.com/react-dnd/react-dnd/issues/1451
  const dragDropRef = useCallback(
    node => {
      drag(node);
      drop(node);
    },
    [drag, drop],
  );

  // This is required for creating a custom drag overlay
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  return (
    <TreeItem
      itemId={nodeId}
      onClick={onLabelClick}
      classes={{
        content:
          (isOver && canDrop) || selected
            ? classes.contentHighlighted
            : classes.content,
        label:
          (isOver && canDrop) || selected
            ? classes.labelHighlighted
            : classes.label,
      }}
      slots={{ collapseIcon: CollapseIcon }}
      slotProps={{ collapseIcon: { loading: anthologyCollections.isLoading } }}
      label={
        <SidebarTreeItemLabel
          dropRef={dragDropRef}
          tooltipLabel={tooltipLabel}
          labelText={anthology.name}
          TypographyProps={getTypographyProps("anthology")}
        />
      }
      {...otherProps}
    >
      {notBlank(collections) ? (
        collections.map(collection => (
          <SidebarCollectionTreeItem
            child={collection}
            key={buildAnthologyChildNodeId({
              module: parent,
              anthology,
              collection,
            })}
            nodeId={buildAnthologyChildNodeId({
              module: parent,
              anthology,
              collection,
            })}
            showAlert={showAlert}
            onLabelClick={handleLabelClick({
              id: collection.id,
              mediaType: "collection",
              parent: anthology,
              prevMediaList: collections,
              viewPath: buildOrganizeViewPath({
                view: COLLECTION_VIEW,
                ids: [parent.id, anthology.id, collection.id],
              }),
            })}
            selected={any(
              propEq("id", collection.id),
              getCollections(selectedContentGroups),
            )}
            tooltipLabel={collection.name}
            label={collection.name}
            parent={anthology}
            parentChildrenList={collections}
          />
        ))
      ) : (
        <div />
      )}
    </TreeItem>
  );
};

export default SidebarAnthologyTreeItem;
